// import React, {
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { apiService } from 'src/utils/apiServices';
import { PHYSICAL_API } from 'src/utils/physical-api';

const BASE = PHYSICAL_API.terms;
const ALL_TERMS = { take: 100 };

const authHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  },
});

const TermsAndConditionsDataContext = createContext(null);
const TermsAndConditionsActionsContext = createContext(null);

export const TermsAndConditionsProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get(BASE, { ...authHeaders(), params: ALL_TERMS });
      setItems(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      toast.error('Error loading terms and conditions');
      console.error('Error fetching terms and conditions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const createItem = useCallback(
    async (payload) => {
      try {
        await apiService.post(
          BASE,
          { title: payload.title, en: payload.en, ka: payload.ka },
          authHeaders()
        );
        toast.success('Terms and conditions created');
        await fetchAll();
        return true;
      } catch (error) {
        toast.error('Error creating terms and conditions');
        console.error('Error creating terms and conditions:', error);
        return false;
      }
    },
    [fetchAll]
  );

  const sendAcceptance = useCallback(async (payload) => {
    try {
      await apiService.post(`${BASE}/send`, payload, authHeaders());
      toast.success('Terms and conditions request sent');
      return true;
    } catch (error) {
      const message =
        error?.response?.data?.message || 'Error sending terms and conditions request';
      toast.error(message);
      console.error('Error sending terms and conditions request:', error);
      return false;
    }
  }, []);

  const activate = useCallback(
    async (id) => {
      setActivating(id);
      try {
        await apiService.patch(`${BASE}/${id}/activate`, null, authHeaders());
        setItems((prev) =>
          prev.map((row) => ({ ...row, active: row.id === id }))
        );
        toast.success('Terms and conditions activated');
        return true;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          'Error activating terms and conditions';
        toast.error(message);
        console.error('Error activating terms and conditions:', error);
        return false;
      } finally {
        setActivating(null);
      }
    },
    []
  );

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const fetchOne = useCallback(async (id) => {
    const { data } = await apiService.get(`${BASE}/${id}`, authHeaders());
    return data;
  }, []);

  const dataValue = useMemo(
    () => ({ items, loading, activating }),
    [items, loading, activating]
  );

  const actionsValue = useMemo(
    () => ({ fetchAll, fetchOne, createItem, activate, sendAcceptance }),
    [fetchAll, fetchOne, createItem, activate, sendAcceptance]
  );

  return (
    <TermsAndConditionsDataContext.Provider value={dataValue}>
      <TermsAndConditionsActionsContext.Provider value={actionsValue}>
        {children}
      </TermsAndConditionsActionsContext.Provider>
    </TermsAndConditionsDataContext.Provider>
  );
};

TermsAndConditionsProvider.propTypes = {
  children: PropTypes.node,
};

export const useTermsAndConditionsData = () =>
  useContext(TermsAndConditionsDataContext);

export const useTermsAndConditionsActions = () =>
  useContext(TermsAndConditionsActionsContext);
